import {_} from 'lodash';
import {IAuthenticationType, ICompanyLogin, IMessageData, IRouteDefinitionParams, IRouteParams} from "../../index";
import * as ko from "knockout";
import {config, global, lm} from "../../appContext";
import {navigateToAppHome} from "../../auth/navigateAfterLogin";
import {nsLanguage} from "../../util/nsLanguage";
import getLanguageProperty = nsLanguage.getLanguageProperty;
import {nsRoute} from "../../util/route";
import {PLEASE_WAIT_MESSAGE} from "../../constants/Language";
import {createErrorMessage, isBit, validateObjectGuid} from "../../util/misc";
import {CompanySelect} from "./company-select";
import {
    ImgBase64_Google,
    ImgBase64_GrandId,
    ImgBase64_IDPorten,
    ImgBase64_Microsoft,
    ImgBase64_Telia,
    ImgBase64_Unknonwn
} from "../../constants/Images";
import {nsLogin} from "../../util/nsLogin";
import {AuthenticationTypeFlag} from "../../constants/AuthenticationTypeFlag";
import {orderLoginChoices} from "./loginOrder";
import {setRouteHash} from "../../constants/RouteDefinitions";
import PromiseRequest from "../../req/PromiseRequest";
import {nsAmplify} from "../../util/nsAmplify";
import {storeObservable} from "../../util/sessionstore";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import formHtml from './form.html?raw';

export let template: string = formHtml;

interface IOptions {
    loginurl: string
    logo: string | null
    name: string
}

interface ICustomAuthenticationType {
    authenticationType: IAuthenticationType
    options: IOptions
}

/** Steg 2 etter valgt organisasjon — styrt av {@link ICompanyLogin}-felt. */
export type TLoginCardAuthMode = 'SSO' | 'SSO_FORGOT' | 'LOCAL';

/** Fast tekst — vises via page-message / lm.t; nøkkel finnes ikke i språk-API, så rå streng brukes. */
const NATIVE_LOGIN_INVALID_CREDENTIALS_MSG = "Ugyldig brukernavn eller passord.";

export class viewModel extends ParsedRouteDataDecorator {
    /** Meldinger øverst i innloggingskortet (lasting, org-valg, generelle feil). */
    readonly cardBannerMessage: KnockoutObservable<IMessageData | null> = ko.observable(null);
    /** Kun lokal innlogging: feil rett over «Logg inn» (nærhetsprinsipp). */
    readonly nativeLoginInlineMessage: KnockoutObservable<IMessageData | null> = ko.observable(null);
    /** Rød ramme på brukernavn/passord ved mislykket autentisering (uten hjelpetekst under feltene). */
    readonly nativeLoginInputsHaveError: KnockoutObservable<boolean> = ko.observable(false);

    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);

    private readonly companySelect = new CompanySelect()

    private req_getauthenticationtypes: PromiseRequest = new PromiseRequest('getauthenticationtypes');
    private readonly req_formlogin: PromiseRequest = new PromiseRequest('formlogin');
    private allAuthenticationTypes!: IAuthenticationType[];
    /** Bumps when {@link allAuthenticationTypes} is set so {@link loginMethodsForView} recomputes. */
    private readonly authTypesRevision: KnockoutObservable<number> = ko.observable(0);

    readonly organizationDisplayName = ko.pureComputed((): string => {
        const c = this.getCompany();
        if (!c?.name || c.hidename) {
            return '';
        }
        return c.name;
    });

    /** Tittel på innloggingssteg (steg 2); viser orgnr når navnet er skjult (hidename). */
    readonly organizationStepHeading = ko.pureComputed((): string => {
        const c = this.getCompany();
        if (!c) {
            return '';
        }
        if (c.hidename) {
            return c.orgnr ?? '';
        }
        return (c.name || c.orgnr || '').trim();
    });

    /** Etter «Fortsett» vises SSO-steg; unngår implisitt overgang ved kun dropdown-valg. */
    private readonly organizationStepConfirmed: KnockoutObservable<boolean> = ko.observable(false);

    /**
     * Steg 2 når organisasjon er bekreftet — må ikke kreve synlig visningsnavn (hidename ga tidligere tom tittel
     * og brukeren ble hengende på steg 1 selv med URL-deep link).
     */
    readonly showSsoStep = ko.pureComputed(
        (): boolean => !!(this.getCompany() && this.organizationStepConfirmed()),
    );
    /** Når avkrysset lagres valgt organisasjon på enheten; ellers kun i økten. */
    readonly rememberOrganizationOnDevice = storeObservable("login.remember.organization", true);

    /** `getcompanylogin` feilet — ikke vis velger / «Husk» / «Fortsett». */
    readonly organizationPickerHasFatalError = ko.pureComputed((): boolean => !!this.companySelect.loadError());

    /** Designs alert (page-message, danger) under organisasjonstittelen. */
    readonly organizationPickerFatalAlert = ko.pureComputed((): IMessageData | null => {
        const err = this.companySelect.loadError();
        if (!err) {
            return null;
        }
        return {message: err, type: "danger"};
    });

    readonly footerYear = ko.pureComputed((): number => new Date().getFullYear());

    /**
     * Markeds-iframe (høyre panel). URL fra {@link Config.URL_LOGIN_MARKETING} via språkvelger
     * ({@link getLanguageProperty}), med engelsk der det ikke finnes oversettelse for valgt språk.
     * Eldre enkeltstreng i config støttes fortsatt.
     */
    readonly loginMarketingIframeSrc = ko.pureComputed((): string => {
        config.isLoaded();
        lm.selectedLanguage();
        const fallback = 'https://content.identum.no/login-page-eadm';
        const marketing = config.URL_LOGIN_MARKETING;
        if (typeof marketing === 'string') {
            return marketing.trim() || fallback;
        }
        if (marketing && typeof marketing === 'object') {
            const url = (getLanguageProperty(marketing) ?? '').trim();
            if (url) {
                return url;
            }
        }
        return fallback;
    });

    private readonly persistOrgChoiceSubs: KnockoutSubscription[] = [];

    readonly nativeUsername: KnockoutObservable<string> = ko.observable('');
    readonly nativePassword: KnockoutObservable<string> = ko.observable('');

    private readonly selectedOrgOrnr = ko.pureComputed((): string | null => this.getCompany()?.orgnr ?? null);

    /**
     * Variant for Steg 2: lokal innlogging, ren SSO, eller SSO med minst én glemt-passord-metode.
     */
    readonly loginCardAuthMode = ko.pureComputed((): TLoginCardAuthMode => {
        const company = this.getCompany();
        if (!company) {
            return 'SSO';
        }
        if (isBit(AuthenticationTypeFlag.EADM_NATIVE, company.authenticationtypes)) {
            return 'LOCAL';
        }
        if (this.companyHasAnyForgotPasswordMethod(company) && this.companyHasAnyFederatedLogin(company)) {
            return 'SSO_FORGOT';
        }
        return 'SSO';
    });

    readonly isLoginCardLocal = ko.pureComputed((): boolean => this.loginCardAuthMode() === 'LOCAL');

    readonly showSsoFederatedBlock = ko.pureComputed((): boolean => {
        if (this.isLoginCardLocal()) {
            return false;
        }
        return this.loginMethodsForView().length > 0;
    });

    readonly showForgotPasswordLink = ko.pureComputed((): boolean => {
        const mode = this.loginCardAuthMode();
        return mode === 'LOCAL' || mode === 'SSO_FORGOT';
    });

    /** Låser skjema under formlogin + profil (doLogin) — umiddelbar knappe-feedback. */
    readonly nativeLoginUiLocked = ko.pureComputed(
        (): boolean => this.isNativeLoginSubmitting() || this.req_formlogin.isRequesting(),
    );

    readonly nativeLoginFieldsEnabled = ko.pureComputed((): boolean => !this.nativeLoginUiLocked());

    readonly nativeLoginCanSubmit = ko.pureComputed((): boolean => {
        const u = this.nativeUsername().trim();
        const p = this.nativePassword();
        return !!u && !!p && !this.nativeLoginUiLocked();
    });

    private readonly isNativeLoginSubmitting: KnockoutObservable<boolean> = ko.observable(false);

    /** Myk utgang av innloggingskort før navigasjon */
    readonly cardTransitionOut: KnockoutObservable<boolean> = ko.observable(false);

    /**
     * Federated / external methods enabled for the selected organization (bitmask on company).
     * Tom ved lokal (native) innlogging — da vises brukernavn/passord i stedet.
     */
    readonly loginMethodsForView = ko.pureComputed((): ICustomAuthenticationType[] => {
        this.authTypesRevision();
        this.companySelect.orgid();
        if (!this.isReady()) {
            return [];
        }
        const company = this.getCompany();
        if (!company) {
            return [];
        }
        if (isBit(AuthenticationTypeFlag.EADM_NATIVE, company.authenticationtypes)) {
            return [];
        }
        return this.buildOptionsForCompany(company);
    });

    readonly hasChosenOrganization = ko.pureComputed((): boolean => !!this.getCompany());

    clearOrganizationChoice = (): void => {
        /* Rydd valg før steg 1 vises igjen — bruk selectedOrg-writer så orgid og store holdes i sync med combobox. */
        this.companySelect.selectedOrg(null);
        this.organizationStepConfirmed(false);
        this.nativeUsername('');
        this.nativePassword('');
        this.cardBannerMessage(null);
        this.clearNativeLoginFieldError();
        setRouteHash('formlogin');
    };

    continueToSsoStep = (): void => {
        const company = this.getCompany();
        if (!company) {
            this.cardBannerMessage({ message: lm.t('choose organization') ?? '', type: 'warning' });
            return;
        }
        this.cardBannerMessage(null);
        this.organizationStepConfirmed(true);
        /* Synk adressefelt med valgt org — `#/formlogin/<orgnr>`; vanity kun når den ikke er 0. */
        const hashParams: IRouteParams = {orgnr: company.orgnr};
        const vanity = company.vanityindex;
        if (vanity !== undefined && vanity !== null && vanity !== 0) {
            hashParams['index'] = vanity;
        }
        setRouteHash('formlogin', hashParams);
    };

    retryOrganizationPickerLoad = (): void => {
        this.companySelect.retryLoad();
    };

    constructor(params: IRouteDefinitionParams) {
        super(params);
        if (global.login.isLoggedIn()) {
            navigateToAppHome();
            return this;
        }

        this.init();

        const selectedCompanyObs = storeObservable("selected.company.login");
        const rememberObs = this.rememberOrganizationOnDevice;
        this.persistOrgChoiceSubs.push(
            selectedCompanyObs.subscribe(() => {
                if (!rememberObs()) {
                    queueMicrotask(() => nsAmplify.store("selected.company.login", null));
                }
            }),
        );
        this.persistOrgChoiceSubs.push(
            rememberObs.subscribe((remember: boolean) => {
                if (remember) {
                    nsAmplify.store("selected.company.login", selectedCompanyObs());
                } else {
                    nsAmplify.store("selected.company.login", null);
                }
            }),
        );
        this.persistOrgChoiceSubs.push(
            this.hasChosenOrganization.subscribe((has: boolean) => {
                if (!has) {
                    this.organizationStepConfirmed(false);
                }
            }),
        );
        this.persistOrgChoiceSubs.push(
            this.selectedOrgOrnr.subscribe(() => {
                this.nativeUsername('');
                this.nativePassword('');
                this.clearNativeLoginFieldError();
            }),
        );
        this.persistOrgChoiceSubs.push(
            this.nativeUsername.subscribe(() => {
                this.clearNativeLoginFieldError();
            }),
        );
        this.persistOrgChoiceSubs.push(
            this.nativePassword.subscribe(() => {
                this.clearNativeLoginFieldError();
            }),
        );
        this.persistOrgChoiceSubs.push(
            this.companySelect.isLoaded.subscribe((loaded: boolean) => {
                if (loaded) {
                    queueMicrotask(() => this.applyPostLoadOrganizationLanding());
                }
            }),
        );
        this.persistOrgChoiceSubs.push(
            global.router.currentRoute.subscribe(() => {
                if (!this.companySelect.isLoaded() || this.companySelect.loadError()) {
                    return;
                }
                queueMicrotask(() => this.applyPostLoadOrganizationLanding());
            }),
        );
    }

    /**
     * Etter org-liste er klar (og ved hash-/ruteendring): `#/formlogin/:orgnr:/:index:` har forrang,
     * deretter «Husk organisasjon».
     */
    private applyPostLoadOrganizationLanding(): void {
        if (!this.companySelect.isLoaded() || this.companySelect.loadError()) {
            return;
        }
        const cr = global.router.currentRoute();
        if (cr?.__routeDef?.id !== "formlogin") {
            return;
        }
        const rp = cr.__routeParams;
        if (rp?.['orgnr'] ?? rp?.['orgid']) {
            this.applyOrgFromRouteParams(rp);
            return;
        }
        this.applyRememberedOrganizationLanding();
    }

    /** Deep link `formlogin/{orgnr}/{index}` — velg org og gå til innloggingssteg uten «Fortsett». */
    private applyOrgFromRouteParams(routeParams: IRouteParams): void {
        const orgnr = routeParams?.['orgnr'] ?? routeParams?.['orgid'];
        if (!orgnr) {
            return;
        }
        const vanity = this.parseRouteVanityIndex(routeParams);
        const resolved = global.companyLogin.resolveCompanyForRoute(orgnr, vanity);
        if (!resolved) {
            this.cardBannerMessage({message: lm.t("choose organization") ?? "", type: "warning"});
            return;
        }
        this.cardBannerMessage(null);
        this.companySelect.mergeOrganizationIntoVisibleList(resolved);
        this.companySelect.selectedOrg(resolved);
        this.organizationStepConfirmed(true);
    }

    private parseRouteVanityIndex(routeParams: IRouteParams | undefined): number {
        if (!routeParams) {
            return 0;
        }
        const raw = routeParams['index'];
        if (raw === undefined || raw === null || raw === "") {
            return 0;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
    }

    /**
     * Med «Husk organisasjon på denne enheten»: etter at org-listen er lastet, gå direkte til innlogging for
     * lagret organisasjon uten å måtte trykke «Fortsett».
     */
    private applyRememberedOrganizationLanding(): void {
        if (!this.rememberOrganizationOnDevice()) {
            return;
        }
        if (!this.companySelect.isLoaded() || this.companySelect.loadError()) {
            return;
        }
        const stored = global.companyLogin.selected();
        if (!stored?.orgnr) {
            return;
        }
        const vanity = stored.vanityindex ?? 0;
        const resolved =
            global.companyLogin.getCompanyById(stored.orgnr, vanity) ??
            global.companyLogin.getCompanyById(stored.orgnr, 0);
        if (!resolved) {
            global.companyLogin.selected(null);
            this.companySelect.orgid(null);
            return;
        }
        global.companyLogin.selected(resolved);
        this.companySelect.orgid(resolved.orgnr);
        this.organizationStepConfirmed(true);
    }

    private clearNativeLoginFieldError(): void {
        this.nativeLoginInputsHaveError(false);
        this.nativeLoginInlineMessage(null);
    }

    private init(): Promise<any> {
        this.isReady(false)
        this.cardBannerMessage({message: PLEASE_WAIT_MESSAGE})
        return Promise.resolve()
            .then(() => {
                return this.req_getauthenticationtypes
                    .request()
                    .then((data: IAuthenticationType[]) => {
                        this.allAuthenticationTypes = data;
                        this.authTypesRevision(this.authTypesRevision() + 1);
                    })
            })
            .then(() => {
                this.cardBannerMessage(null)
                this.isReady(true)
            })
            .catch((reason: unknown) => {
                this.allAuthenticationTypes = []
                this.authTypesRevision(this.authTypesRevision() + 1);
                this.cardBannerMessage(createErrorMessage(String(reason)))
                this.isReady(true)
            })
    }

    private isFederatedOnlyFlag(flag: number): boolean {
        return (
            flag !== AuthenticationTypeFlag.EADM_NATIVE &&
            flag !== AuthenticationTypeFlag.EADM_NATIVE_SMS
        );
    }

    private companyHasAnyFederatedLogin(company: ICompanyLogin): boolean {
        if ((company.identityproviders ?? []).some(provider => !!provider.loginurl)) {
            return true;
        }
        return _(this.allAuthenticationTypes ?? []).some(
            (o: IAuthenticationType) =>
                this.isFederatedOnlyFlag(o.flag) &&
                o.flag !== AuthenticationTypeFlag.CUSTOM_SAML &&
                isBit(o.flag, company.authenticationtypes) &&
                o.name !== 'eAdm native SMS',
        );
    }

    private companyHasAnyForgotPasswordMethod(company: ICompanyLogin): boolean {
        return !!(
            company.forgotpassordidporten
            || company.forgotpassordsms
            || company.forgotpassordbankidse
            || company.forgotpassordteliafi
        );
    }

    private buildOptionsForCompany(company: ICompanyLogin): ICustomAuthenticationType[] {
        const catalog = _(this.allAuthenticationTypes ?? [])
            .filter(
                (o: IAuthenticationType) =>
                    this.isFederatedOnlyFlag(o.flag) &&
                    o.flag !== AuthenticationTypeFlag.CUSTOM_SAML &&
                    isBit(o.flag, company.authenticationtypes) &&
                    o.name != 'eAdm native SMS',
            )
            .sortBy([
                (o: IAuthenticationType) => (company.authenticationid === o.id ? 0 : 1),
                (o: IAuthenticationType) => (o.flag === AuthenticationTypeFlag.GOOGLE ? 0 : 1),
                (o: IAuthenticationType) => o.name.toLowerCase(),
            ])
            .map((o: IAuthenticationType) => ({
                authenticationType: o,
                options: this.getLoginOption(o, company),
            }))
            .value();

        const custom = (company.identityproviders ?? [])
            .filter(provider => !!provider.loginurl)
            .map(provider => {
                const authenticationType: IAuthenticationType = {
                    id: provider.id,
                    name: provider.displayname,
                    loginurl: provider.loginurl,
                    flag: AuthenticationTypeFlag.CUSTOM_SAML,
                    twofactor: null as unknown as IAuthenticationType['twofactor'],
                };
                return {
                    authenticationType,
                    options: {
                        loginurl: nsLogin.getFederatedLoginUrl(authenticationType, company.orgnr),
                        logo: null,
                        name: provider.displayname,
                    },
                };
            });

        const customSamlCatalogId = (this.allAuthenticationTypes ?? [])
            .find(type => type.flag === AuthenticationTypeFlag.CUSTOM_SAML)?.id;
        return orderLoginChoices(catalog, custom, company.authenticationid, customSamlCatalogId);
    }

    private getLoginOption(authenticationType: IAuthenticationType, company: ICompanyLogin): IOptions {
        const loginurl = nsLogin.getFederatedLoginUrl(authenticationType, company.orgnr)

        const ret: IOptions = {
            loginurl: loginurl,
            logo: null,
            name: authenticationType.name,
        }

        switch (authenticationType.flag) {
            case AuthenticationTypeFlag.AAD_FEDERATION: {
                ret.name = 'Microsoft'
                ret.logo = ImgBase64_Microsoft
                break
            }
            case AuthenticationTypeFlag.ID_PORTEN: {
                ret.name = 'ID-porten'
                ret.logo = ImgBase64_IDPorten
                break
            }
            case AuthenticationTypeFlag.GOOGLE: {
                ret.name = 'Google'
                ret.logo = ImgBase64_Google
                break
            }
            case AuthenticationTypeFlag.TELIA_FINLAND: {
                ret.name = 'Telia'
                ret.logo = ImgBase64_Telia
                break
            }
            case AuthenticationTypeFlag.GRAND_ID_BANKID_SE: {
                ret.name = 'GrandID BankID SE'
                ret.logo = ImgBase64_GrandId
                break
            }
            default: {
                ret.name = authenticationType.name
                ret.logo = ImgBase64_Unknonwn
                break
            }
        }

        return ret
    }

    private getCompany = ko.pureComputed((): ICompanyLogin | undefined => {
        const company = global.companyLogin.selected();
        if (company) {
            return company
        } else {
            const orgid = this.companySelect.orgid()
            return orgid ? global.companyLogin.getCompanyById(orgid) : undefined
        }
    })

    onSubmit(customAuthenticationType: ICustomAuthenticationType): boolean {
        if (!this.getCompany()) {
            this.cardBannerMessage({ message: lm.t('choose organization') ?? '', type: 'warning' });
            return false;
        }
        window.location.href = customAuthenticationType.options.loginurl;
        return false;
    }

    goForgotPassword = (): boolean => {
        const company = this.getCompany();
        if (!company) {
            return false;
        }
        setRouteHash('forgotpassword', {orgnr: company.orgnr});
        return false;
    };

    private static readonly LOGIN_CARD_EXIT_MS = 280;

    onNativeLoginSubmit = (): boolean => {
        const company = this.getCompany();
        if (!company || !this.isLoginCardLocal()) {
            return false;
        }
        const username = this.nativeUsername().trim();
        const password = this.nativePassword();
        if (!username || !password) {
            this.clearNativeLoginFieldError();
            this.cardBannerMessage({message: '((please enter username and password))', type: 'warning'});
            return false;
        }
        this.cardBannerMessage(null);
        this.nativeLoginInlineMessage(null);
        this.nativeLoginInputsHaveError(false);
        this.isNativeLoginSubmitting(true);
        this.req_formlogin
            .request({
                orgid: company.orgnr,
                username,
                password,
                captchacode: '',
            })
            .then((data: unknown) => {
                const token = this.unwrapAuthToken(data);
                global.login.setToken(token);
                /* Fade only after profile bootstrap, immediately before leaving formlogin. */
                return global.login.doLogin({
                    skipLoadingBanner: true,
                    beforeNavigate: () => {
                        this.cardTransitionOut(true);
                        return new Promise<void>(resolve => {
                            window.setTimeout(resolve, viewModel.LOGIN_CARD_EXIT_MS);
                        });
                    },
                });
            })
            .catch((reason: unknown) => {
                this.cardTransitionOut(false);
                global.messageData(null);
                const reasonStr = String(reason ?? "").trim();
                if (validateObjectGuid(reasonStr)) {
                    this.nativeLoginInlineMessage({
                        message: NATIVE_LOGIN_INVALID_CREDENTIALS_MSG,
                        type: "danger",
                    });
                    this.nativeLoginInputsHaveError(true);
                } else {
                    this.nativeLoginInlineMessage(null);
                    this.nativeLoginInputsHaveError(false);
                    this.cardBannerMessage(createErrorMessage(reasonStr));
                }
            })
            .finally(() => {
                this.isNativeLoginSubmitting(false);
            });
        return false;
    };

    private unwrapAuthToken(data: unknown): string {
        if (data == null || data === '') {
            throw new Error('empty authentication response');
        }
        if (typeof data === 'string') {
            return data;
        }
        if (Array.isArray(data)) {
            const first = data[0];
            if (typeof first === 'string') {
                return first;
            }
            if (first && typeof first === 'object' && 'token' in first) {
                return String((first as {token: unknown}).token);
            }
        }
        if (typeof data === 'object' && data !== null && 'token' in data) {
            return String((data as {token: unknown}).token);
        }
        throw new Error('unexpected authentication response');
    }

    override dispose() {
        this.persistOrgChoiceSubs.forEach(s => s.dispose());
        super.dispose();
    }

}
